"use client";
import { useEffect, useState, useCallback } from "react";
import { subscribeToBanners } from "@/firebase/firestoreServices";
import { SkeletonBanner } from "./SkeletonLoaders";
import type { Banner } from "@/types";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import Image from "next/image";

const SLIDE_INTERVAL = 4500;

// Enhanced slide variants with smoother transitions
const variants = {
  enter: (dir: number) => ({
    x: dir > 0 ? "100%" : "-100%",
    opacity: 0,
    scale: 1.08,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 260,
      damping: 30,
      opacity: { duration: 0.4 },
    },
  },
  exit: (dir: number) => ({
    x: dir > 0 ? "-50%" : "50%",
    opacity: 0,
    scale: 0.94,
    transition: {
      duration: 0.45,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  }),
};

interface BannerSliderProps {
  initialBanners?: Banner[];
}

const BannerSlider = ({ initialBanners = [] }: BannerSliderProps) => {
  const [banners, setBanners] = useState<Banner[]>(initialBanners);
  const [loading, setLoading] = useState(initialBanners.length === 0);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  useEffect(() => {
    const unsub = subscribeToBanners((data) => {
      setBanners(data as Banner[]);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const advance = useCallback(
    (dir: number) => {
      setDirection(dir);
      setCurrent((c) => (c + dir + banners.length) % banners.length);
      setProgress(0);
    },
    [banners.length]
  );

  // Auto-play + progress bar
  useEffect(() => {
    if (banners.length <= 1 || paused || userPaused) return;

    const step = 50;
    const ticks = SLIDE_INTERVAL / step;
    let tick = 0;

    const timer = setInterval(() => {
      tick++;
      setProgress((tick / ticks) * 100);
      if (tick >= ticks) {
        advance(1);
        tick = 0;
      }
    }, step);

    return () => clearInterval(timer);
  }, [banners.length, paused, userPaused, advance, current]);

  const togglePause = () => {
    setUserPaused(!userPaused);
  };

  if (loading) return <SkeletonBanner />;
  if (banners.length === 0) return null;

  return (
    <div
      className="relative w-full overflow-hidden rounded-3xl shadow-2xl group select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* ── Slide container ── */}
      <div className="relative aspect-[2.2/1] md:aspect-[3/1] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={banners[current]?.id}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0 w-full h-full"
          >
            <Image
              src={banners[current]?.imageUrl}
              alt={`Banner ${current + 1}`}
              fill
              priority={current === 0}
              fetchPriority={current === 0 ? "high" : "auto"}
              className="object-cover"
              draggable={false}
              sizes="100vw"
            />
          </motion.div>
        </AnimatePresence>

        {/* Enhanced gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10 pointer-events-none" />

      </div>

      {/* ── Navigation Controls ── */}
      {banners.length > 1 && (
        <>
          {/* Previous Button */}
          <motion.button
            onClick={() => advance(-1)}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20
              bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl 
              border border-white/40 dark:border-gray-700/40
              rounded-full p-3 shadow-2xl
              opacity-0 group-hover:opacity-100 
              transition-all duration-300
              hidden md:flex items-center justify-center
              hover:bg-white dark:hover:bg-gray-800"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800 dark:text-white" strokeWidth={2.5} />
          </motion.button>

          {/* Next Button */}
          <motion.button
            onClick={() => advance(1)}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20
              bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl 
              border border-white/40 dark:border-gray-700/40
              rounded-full p-3 shadow-2xl
              opacity-0 group-hover:opacity-100 
              transition-all duration-300
              hidden md:flex items-center justify-center
              hover:bg-white dark:hover:bg-gray-800"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 text-gray-800 dark:text-white" strokeWidth={2.5} />
          </motion.button>

        </>
      )}


    </div>
  );
};

export default BannerSlider;